import { useGetCalendarV2Events } from "@/shared/api/economic-calendar-v2/useGetCalendarV2Events";
import {
  CalendarV2Event,
  CalendarV2DataEvent,
  isDataEvent,
  getEventLocation,
  getEventImportance,
  getEventTitle,
} from "@/shared/models/economic-calendar-v2/CalendarV2Event";
import * as Sentry from "@sentry/react";
import {
  countriesList,
  Country,
  getCountryDisplayLabel,
  ImportanceOption,
  importanceOptions,
} from "@/shared/models/economic-calendar-v2";
import { getFlagEmoji } from "@/utils/other";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useStateWithOptionalPersistence } from "@/hooks/useStateWithOptionalPersistence";
import EconomicCalendarV2Countdown from "../EconomicCalendarV2Countdown";
import { formatLocalDate, formatTo24HourTime } from "@/utils/date-utils";
import clsx from "clsx";
import { BgOverlay } from "@/widget-components/firehose-widgets/firehose-all/components";
import {
  getArrowAndColor,
  formatValueWithUnit,
  isEventInPast,
} from "./rows/common";
import { ClassificationBadge } from "./rows/presentational";
import { EarningRowCells } from "./rows/EarningRow";
import { SupplyRowCells } from "./rows/SupplyRow";
import { SpeakerRowCells } from "./rows/SpeakerRow";

interface EconomicCalendarV2TableProps {
  headRowClassNames?: string;
  bodyRowClassNames?: string;
  headCellClassNames?: string;
  bodyCellClassNames?: string;

  /**
   * Per-instance identity when rendered as a dashboard widget. Two copies of
   * the calendar on one tab used to share the persisted country filter (and
   * even live-sync it across popout windows via storage events) because the
   * storage key was a static string.
   */
  widgetCompositeId?: string;
}

const CLASSIFICATION_LABELS: Record<string, string> = {
  expiry: "Expiry",
  holiday: "Holiday",
  ad_hoc: "Ad Hoc",
};

/** Country flag <img> (external CDN, same source as the legacy table). */
function Flag({ code }: { code?: string }) {
  if (!code) return null;
  return (
    <img
      alt={code}
      src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${code}.svg`}
      className="size-4"
    />
  );
}

/** EVENT + 7 figure columns for a `data` (economic release) event. */
function DataRowCells({ event }: { event: CalendarV2DataEvent }) {
  const unit = event.category?.unit;
  const { color, arrow } = getArrowAndColor(event.actual, event.expected);

  return (
    <>
      <td className="px-1 whitespace-normal leading-normal min-w-[150px]">
        <p>
          <span className="text-gray-500 dark:text-gray-400">
            {getCountryDisplayLabel(getEventLocation(event))}{" "}
          </span>
          {getEventTitle(event)} ({event.survey_date || "N/A"})
        </p>
      </td>
      <td
        className={`whitespace-nowrap px-1 text-xs font-extrabold bg-squawk-blue/5 ${color}`}
      >
        {formatValueWithUnit(event.actual, unit)} {arrow}
      </td>
      <td className="whitespace-nowrap px-1 ">
        {formatValueWithUnit(event.expected, unit)}
      </td>
      <td className="whitespace-nowrap px-1 ">
        {formatValueWithUnit(event.low, unit)}
      </td>
      <td className="whitespace-nowrap px-1 ">
        {formatValueWithUnit(event.high, unit)}
      </td>
      <td className="whitespace-nowrap px-1 ">
        {formatValueWithUnit(event.previous, unit)}
      </td>
      <td className="whitespace-nowrap px-1 ">
        {formatValueWithUnit(event.revised, unit)}
      </td>
      <td className="whitespace-nowrap px-1 ">{event.category?.importance}</td>
    </>
  );
}

/** EVENT + spanning detail cell for expiry / holiday / ad_hoc events. */
function SimpleRowCells({ event }: { event: CalendarV2Event }) {
  return (
    <>
      <td className="px-1 whitespace-normal leading-normal min-w-[150px]">
        <p>
          <ClassificationBadge
            label={CLASSIFICATION_LABELS[event.classification] ?? "Event"}
          />
          {getEventTitle(event)}
        </p>
      </td>
      <td colSpan={7} className="px-1 whitespace-nowrap text-gray-500 dark:text-gray-400">
        {event.detail || ""}
      </td>
    </>
  );
}

/** Dispatch the trailing cells (EVENT onward) by classification. */
function RowCells({ event }: { event: CalendarV2Event }) {
  switch (event.classification) {
    case "data":
      return <DataRowCells event={event} />;
    case "earning":
      return <EarningRowCells event={event} />;
    case "supply":
      return <SupplyRowCells event={event} />;
    case "speaker":
      return <SpeakerRowCells event={event} />;
    default:
      return <SimpleRowCells event={event} />;
  }
}

// ---------------- Mobile view ----------------

interface MobileCalendarViewProps {
  events: CalendarV2Event[] | undefined;
  highlightedEvent: CalendarV2Event | null;
}

function MobileCalendarView({ events, highlightedEvent }: MobileCalendarViewProps) {
  const [expandedUuid, setExpandedUuid] = useState<string | null>(null);

  if (!events) return null;

  return (
    <div className="sm:hidden">
      <div className="sticky top-0 z-10 flex items-center gap-2 px-2 py-1.5 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
        <span className="w-10 shrink-0">Time</span>
        <span className="w-5 shrink-0"></span>
        <span className="flex-1">Event</span>
        <span className="shrink-0">Act / Exp</span>
        <span className="w-4 shrink-0"></span>
      </div>

      <div>
        {events.map((event, idx) => {
          const formattedTime = formatTo24HourTime(event.occurs_at);
          const isPast = isEventInPast(event.occurs_at);
          const isExpanded = expandedUuid === event.uuid;
          const isFirstUpcoming =
            idx > 0 && isEventInPast(events[idx - 1].occurs_at) && !isPast;

          const data = isDataEvent(event) ? event : undefined;
          const unit = data?.category?.unit;
          const { color, arrow } = getArrowAndColor(
            data?.actual,
            data?.expected
          );

          return (
            <React.Fragment key={event.uuid}>
              {isFirstUpcoming && (
                <div className="py-2 relative">
                  <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[1px] bg-gradient-to-r from-squawk-blue/0 via-squawk-blue to-squawk-blue/0">
                    <div className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-900 px-2 text-squawk-blue text-[10px] font-semibold tracking-wide">
                      UPCOMING
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={() =>
                  setExpandedUuid(isExpanded ? null : event.uuid)
                }
                className={clsx(
                  "w-full text-left px-2 py-2 transition-colors rounded-none border-t border-gray-200 dark:border-gray-700",
                  isPast
                    ? "bg-gray-50 dark:bg-gray-800/30"
                    : "bg-white dark:bg-gray-900",
                  highlightedEvent?.uuid === event.uuid && "bg-terminal-orange/20"
                )}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400 w-10 shrink-0 font-mono">
                    {formattedTime}
                  </span>
                  <span className="w-5 shrink-0 flex justify-center">
                    <Flag code={getEventLocation(event)} />
                  </span>
                  <span className="flex-1 text-xs text-gray-900 dark:text-gray-100 truncate">
                    {getEventTitle(event)}
                  </span>
                  <div className="flex items-center gap-1 shrink-0">
                    <span className={clsx("text-xs font-bold", color)}>
                      {formatValueWithUnit(data?.actual, unit) || "—"}
                      {arrow && <span className="ml-0.5">{arrow}</span>}
                    </span>
                    <span className="text-gray-400 dark:text-gray-500 text-xs">
                      /
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {formatValueWithUnit(data?.expected, unit) || "—"}
                    </span>
                  </div>
                  <svg
                    className={clsx(
                      "w-4 h-4 text-gray-400 transition-transform shrink-0",
                      isExpanded && "rotate-180"
                    )}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </button>

              {isExpanded && (
                <div className="px-2 py-3 bg-gray-50 dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700">
                  <p className="text-xs text-gray-900 dark:text-gray-100 font-medium mb-3">
                    {getCountryDisplayLabel(getEventLocation(event))}{" "}
                    {getEventTitle(event)}
                    {data?.survey_date && (
                      <span className="text-gray-500 dark:text-gray-400 font-normal">
                        {" "}
                        ({data.survey_date})
                      </span>
                    )}
                  </p>

                  {data ? (
                    <>
                      <div className="grid grid-cols-4 gap-2 text-center">
                        <MobileStat label="Act" value={formatValueWithUnit(data.actual, unit)} className={color} />
                        <MobileStat label="Exp" value={formatValueWithUnit(data.expected, unit)} />
                        <MobileStat label="Prev" value={formatValueWithUnit(data.previous, unit)} />
                        <MobileStat label="Impact" value={data.category?.importance} capitalize />
                      </div>
                      <div className="grid grid-cols-4 gap-2 text-center mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                        <MobileStat label="Low" value={formatValueWithUnit(data.low, unit)} />
                        <MobileStat label="High" value={formatValueWithUnit(data.high, unit)} />
                        <MobileStat label="Rev" value={formatValueWithUnit(data.revised, unit)} />
                        <MobileStat label="GMT" value={formatTo24HourTime(event.occurs_at, "GMT")} />
                      </div>
                    </>
                  ) : (
                    <p className="text-xs text-gray-600 dark:text-gray-300">
                      {CLASSIFICATION_LABELS[event.classification] ??
                        event.classification}
                      {event.detail ? ` — ${event.detail}` : ""}
                    </p>
                  )}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function MobileStat({
  label,
  value,
  className,
  capitalize,
}: {
  label: string;
  value: React.ReactNode;
  className?: string;
  capitalize?: boolean;
}) {
  return (
    <div>
      <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">
        {label}
      </p>
      <p
        className={clsx(
          "text-xs",
          className ?? "text-gray-700 dark:text-gray-300",
          capitalize && "capitalize"
        )}
      >
        {value || "—"}
      </p>
    </div>
  );
}

// ---------------- Main table ----------------

function EconomicCalendarV2Table(props: EconomicCalendarV2TableProps) {
  const [eventCalendarDate, setEventCalendarDate] = React.useState(
    () => new Date()
  );

  const [highlightedEvent, setHighlightedEvent] =
    React.useState<CalendarV2Event | null>(null);

  const [selectedImportance, setSelectedImportance] = React.useState<
    ImportanceOption[]
  >([]);

  const [selectedCountries, setSelectedCountries] =
    useStateWithOptionalPersistence<Country[]>(
      // Scoped to this instance on a dashboard; the standalone page passes no
      // composite id and keeps the legacy shared key.
      props.widgetCompositeId
        ? `economic-calendar-v2.selectedCountries:${props.widgetCompositeId}`
        : "economic-calendar-v2.selectedCountries",
      []
    );

  const { data } = useGetCalendarV2Events(eventCalendarDate);

  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
  const containerRef = useRef<HTMLDivElement | null>(null);
  const stickyHeaderRef = useRef<HTMLDivElement | null>(null);
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);

  useEffect(() => {
    const el = stickyHeaderRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setStickyHeaderHeight(entry.contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const filteredData = useMemo(() => {
    if (!data) return undefined;

    let result = data;

    if (selectedImportance.length > 0) {
      result = result.filter((event) => {
        const importance = getEventImportance(event);
        return selectedImportance.some(
          (option) =>
            option.value.toLocaleLowerCase() ===
            importance?.toLocaleLowerCase()
        );
      });
    }

    if (selectedCountries.length > 0) {
      result = result.filter((event) =>
        selectedCountries.some(
          (country) => country.id === getEventLocation(event)
        )
      );
    }

    return result;
  }, [data, selectedImportance, selectedCountries]);

  const scrollToRow = (uuid: string) => {
    const row = rowRefs.current[uuid];
    const container = containerRef.current;
    const stickyHeader = stickyHeaderRef.current;

    if (!row || !container) return;

    const headerHeight = stickyHeader?.offsetHeight ?? 0;

    let scrollEl: HTMLElement = container;
    if (container.scrollHeight <= container.clientHeight) {
      let parent = container.parentElement;
      while (parent) {
        if (parent.scrollHeight > parent.clientHeight) {
          scrollEl = parent;
          break;
        }
        parent = parent.parentElement;
      }
    }

    const rowRect = row.getBoundingClientRect();
    const scrollRect = scrollEl.getBoundingClientRect();
    const scrollTop =
      scrollEl.scrollTop + (rowRect.top - scrollRect.top) - headerHeight;

    scrollEl.scrollTo({ top: scrollTop, behavior: "smooth" });
  };

  useEffect(() => {
    if (highlightedEvent) {
      const timerId = setTimeout(() => setHighlightedEvent(null), 1500 * 2);
      return () => clearTimeout(timerId);
    }
  }, [highlightedEvent]);

  return (
    <div
      className="bg-white dark:bg-gray-900 p-2 pb-0 sm:pb-[23px] text-gray-900 dark:text-white flex-1 min-h-0 overflow-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-500 transition-colors"
      ref={containerRef}
    >
      {/* Sticky header: Filters + Countdown */}
      <div
        ref={stickyHeaderRef}
        className="sticky -top-2 z-[6] bg-white dark:bg-gray-900 -mx-2 px-2 pt-2 -mt-2 pb-1 border-b border-gray-200 dark:border-gray-700"
      >
        <section className="flex gap-2 items-center pb-2">
          <div className="flex-1 min-w-20">
            <Listbox
              value={selectedCountries}
              onChange={setSelectedCountries}
              multiple
            >
              <ListboxButton className="grid w-full cursor-default grid-cols-1 rounded-md bg-white dark:bg-gray-800 py-0.5 pl-1 pr-1.5 text-left text-gray-900 dark:text-gray-100 outline outline-1 -outline-offset-1 outline-gray-300 dark:outline-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-squawk-blue sm:text-sm/6">
                <span className="col-start-1 row-start-1 truncate pr-6">
                  {selectedCountries?.map((country) => country.name).join(", ") ||
                    "Filter by Country"}
                </span>
                <ChevronUpDownIcon
                  aria-hidden="true"
                  className="col-start-1 row-start-1 size-5 self-center justify-self-end text-gray-500 dark:text-gray-400 sm:size-4"
                />
              </ListboxButton>
              <ListboxOptions
                anchor={{ to: "bottom start", gap: 4, padding: 8 }}
                transition
                className="z-10 max-h-60 w-[var(--button-width)] overflow-auto rounded-md bg-white dark:bg-gray-800 py-1 text-base shadow-lg ring-1 ring-black/5 dark:ring-gray-600 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm"
              >
                {countriesList.map((country) => (
                  <ListboxOption
                    key={country.id}
                    value={country}
                    className="group relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900 dark:text-gray-100 data-[focus]:bg-squawk-blue data-[focus]:text-white data-[focus]:outline-none"
                  >
                    <span className="block truncate font-normal group-data-[selected]:font-semibold ">
                      <span className="mr-2">{getFlagEmoji(country.id)}</span>
                      {country.name}
                    </span>
                    <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-squawk-blue group-[&:not([data-selected])]:hidden group-data-[focus]:text-white">
                      <CheckIcon aria-hidden="true" className="size-5 " />
                    </span>
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </Listbox>
          </div>

          {selectedCountries.length > 0 && (
            <button
              onClick={() => setSelectedCountries([])}
              className="shrink-0 rounded-md bg-white dark:bg-gray-800 py-0.5 px-2 text-xs text-gray-500 dark:text-gray-400 outline outline-1 -outline-offset-1 outline-gray-300 dark:outline-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 sm:text-sm/6"
            >
              Clear
            </button>
          )}

          <input
            type="date"
            name="eventCalendarDate"
            autoComplete="off"
            className="text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded py-0.5 px-1.5 flex-1"
            defaultValue={formatLocalDate(eventCalendarDate)}
            onChange={(e) =>
              setEventCalendarDate(new Date(e.target.value + "T00:00:00"))
            }
          />

          <div className="flex-1 min-w-20">
            <Listbox
              value={selectedImportance}
              onChange={setSelectedImportance}
              multiple
            >
              <ListboxButton className="grid w-full cursor-default grid-cols-1 rounded-md bg-white dark:bg-gray-800 py-0.5 pl-1 pr-1.5 text-left text-gray-900 dark:text-gray-100 outline outline-1 -outline-offset-1 outline-gray-300 dark:outline-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-squawk-blue sm:text-sm/6">
                <span className="col-start-1 row-start-1 truncate pr-6">
                  {selectedImportance?.map((option) => option.value).join(", ") ||
                    "Select Importance"}
                </span>
                <ChevronUpDownIcon
                  aria-hidden="true"
                  className="col-start-1 row-start-1 size-5 self-center justify-self-end text-gray-500 dark:text-gray-400 sm:size-4"
                />
              </ListboxButton>
              <ListboxOptions
                anchor={{ to: "bottom start", gap: 4, padding: 8 }}
                transition
                className="z-10 max-h-60 w-[var(--button-width)] overflow-auto rounded-md bg-white dark:bg-gray-800 py-1 text-base shadow-lg ring-1 ring-black/5 dark:ring-gray-600 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm"
              >
                {importanceOptions.map((option) => (
                  <ListboxOption
                    key={option.id}
                    value={option}
                    className="group relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900 dark:text-gray-100 data-[focus]:bg-squawk-blue data-[focus]:text-white data-[focus]:outline-none"
                  >
                    <span className="block truncate font-normal group-data-[selected]:font-semibold">
                      {option.value}
                    </span>
                    <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-squawk-blue group-[&:not([data-selected])]:hidden group-data-[focus]:text-white">
                      <CheckIcon aria-hidden="true" className="size-5 " />
                    </span>
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </Listbox>
          </div>
        </section>

        <Sentry.ErrorBoundary
          fallback={<p className="text-red-500 text-xs">Calendar error</p>}
        >
          <EconomicCalendarV2Countdown
            onClick={(e) => {
              scrollToRow(e.uuid);
              setHighlightedEvent({ ...e });
            }}
          />
        </Sentry.ErrorBoundary>
      </div>

      {/* Desktop Table */}
      <table className="hidden sm:table min-w-full divide-y divide-gray-300 dark:divide-gray-400 overflow-x-scroll">
        <thead
          className="bg-gray-100 dark:bg-gray-800 sticky py-2 z-[5]"
          style={{ top: stickyHeaderHeight }}
        >
          <tr className={clsx(props.headRowClassNames)}>
            <th className="px-1 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 ">
              TIME
            </th>
            <th className="px-1 text-xs font-semibold text-gray-600 dark:text-gray-300 text-center min-w-20">
              TIME GMT
            </th>
            <th className="px-1 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">
              COUNTRY
            </th>
            <th className="px-1  text-left text-xs font-semibold text-gray-600 dark:text-gray-300">
              EVENT
            </th>
            <th className="px-1  text-left text-xs font-semibold text-gray-600 dark:text-gray-300 bg-squawk-blue/10">
              ACT
            </th>
            <th className="px-1  text-left text-xs font-semibold text-gray-600 dark:text-gray-300">
              EXP
            </th>
            <th className="px-1  text-left text-xs font-semibold text-gray-600 dark:text-gray-300">
              LOW
            </th>
            <th className="px-1  text-left text-xs font-semibold text-gray-600 dark:text-gray-300">
              HIGH
            </th>
            <th className="px-1  text-left text-xs font-semibold text-gray-600 dark:text-gray-300">
              PREV
            </th>
            <th className="px-1  text-left text-xs font-semibold text-gray-600 dark:text-gray-300">
              REV FROM
            </th>
            <th className="px-1  text-left text-xs font-semibold text-gray-600 dark:text-gray-300">
              IMPACT
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
          {filteredData?.length === 0 && (
            <tr>
              <td
                colSpan={11}
                className="text-center text-gray-400 dark:text-gray-500 text-xs py-8"
              >
                No calendar events match the selected filters.
              </td>
            </tr>
          )}
          {filteredData?.map((event, idx) => {
            const formattedTimeString = formatTo24HourTime(event.occurs_at);
            const gmtTimeString = formatTo24HourTime(event.occurs_at, "GMT");

            const isPast = isEventInPast(event.occurs_at);
            const isFirstUpcoming =
              idx > 0 &&
              isEventInPast(filteredData[idx - 1].occurs_at) &&
              !isPast;

            return (
              <React.Fragment key={event.uuid}>
                {isFirstUpcoming && (
                  <tr>
                    <td colSpan={11} className="py-2 relative">
                      <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[1px] bg-gradient-to-r from-squawk-blue/0 via-squawk-blue via-50% to-squawk-blue/0">
                        <div className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-900 px-2 text-squawk-blue text-[10px] font-semibold tracking-wide">
                          UPCOMING
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                <tr
                  ref={(el) => (rowRefs.current[event.uuid] = el)}
                  className={clsx(
                    "relative",
                    isPast
                      ? "bg-white dark:bg-gray-800/30"
                      : "bg-white dark:bg-gray-900",
                    props.bodyRowClassNames
                  )}
                >
                  <td
                    className={clsx(
                      "whitespace-nowrap px-1 ",
                      props.bodyCellClassNames
                    )}
                  >
                    {formattedTimeString}
                  </td>
                  <td className="whitespace-nowrap px-1   text-center">
                    {gmtTimeString}
                  </td>
                  <td className="whitespace-nowrap px-1  text-center">
                    <span className="flex justify-center">
                      <Flag code={getEventLocation(event)} />
                    </span>
                  </td>

                  <RowCells event={event} />

                  {highlightedEvent?.uuid === event.uuid && (
                    <BgOverlay
                      duration={3000}
                      startTime={new Date()}
                      delay={0}
                      color="#FF6600"
                    />
                  )}
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {/* Mobile View */}
      <MobileCalendarView
        events={filteredData}
        highlightedEvent={highlightedEvent}
      />
    </div>
  );
}

export default EconomicCalendarV2Table;
